import * as React from 'react';
import { CSSProperties, DragEventHandler, FocusEventHandler, KeyboardEventHandler } from 'react';
import * as classNames from 'classnames';
import './styles.scss';
import Icon, { IconsAvailable } from '../Icon';
import Tooltip from '../Tooltip';
import { Popover, Position as BlueprintPosition } from '@blueprintjs/core';

export interface IButtonProps {
    /** style to pass */
    style?: CSSProperties;
    /** size to use */
    size?: 'default' | 'small';
    /** type of element to use */
    type?: 'button' | 'submit' | 'anchor';
    /** click event, should not be used with type 'anchor' */
    onClick?: (event) => void;
    /** hyperlink, should only be used with type 'anchor' */
    href?: string;
    /** style to use */
    layout?: 'default' | 'link' | 'icon';
    /** intent variation */
    intent?: 'default' | 'primary' | 'success' | 'danger' | 'discrete' | 'upfront' | 'navy' | 'discrete-navy' | 'grey' | 'white' | 'transparent';
    /** title to use */
    title: React.ReactNode | string;
    /** disable clicking */
    disabled?: boolean;
    /** disable clicking and display loader */
    loading?: boolean;
    /** DOM target element name attribute */
    name?: string;
    /** DOM target element id attribute */
    id?: string;
    /** React-router link component to use instead of HTML A tag. If defined href is passed as "to" prop. */
    link?: any;
    /** Icon to display */
    icon?: IconsAvailable;
    /** Dropdown menu content */
    menu?: React.ReactElement;
    /** className to append */
    className?: string;
    /** Tooltip text to display on hover */
    tooltip?: string;

    onBlur?: FocusEventHandler;
    onDragEnter?: DragEventHandler;
    onDragLeave?: DragEventHandler;
    onDragOver?: DragEventHandler;
    onDrop?: DragEventHandler;
    onFocus?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
}

interface IState {
    isOpen: boolean;
}

/**
 * Displays button
 */
class Button extends React.Component<IButtonProps, IState> {
    static displayName: string = 'Button';

    private innerRef;

    constructor (props: IButtonProps) {
        super(props);
        this.state = {
            isOpen: false,
        };
    }

    handleClosePopover = (e?: React.SyntheticEvent<HTMLElement>) => {
        this.setState({
            isOpen: false,
        });
    };

    handleToggle = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: true,
        });
    };

    /**
     * Focuses button element
     * @public
     */
    focus = () => {
        if (this.innerRef) {
            this.innerRef.focus();
        }
    };

    /**
     * Blurs button element
     * @public
     */
    blur = () => {
        if (this.innerRef) {
            this.innerRef.focus();
            this.innerRef.blur();
        }
    };

    RenderButtonContent = () => {
        return (
            <React.Fragment>
                    <span className="button-inner">
                        {this.props.icon && !this.props.loading && (
                            <Icon
                                className="icon"
                                width={14}
                                height={14}
                                kind={this.props.icon}
                            />
                        )}
                        <span className="text">{this.props.title}</span>
                    </span>
                {this.props.loading && (
                    <div className="loader">
                        <Icon width={14} height={14} kind="loading"/>
                    </div>
                )}
            </React.Fragment>
        );
    };

    render () {
        let Elem: any = 'button';
        if (this.props.type && this.props.type === 'anchor') {
            if (this.props.link) {
                Elem = this.props.link;
            } else {
                Elem = 'a';
            }
        }

        return (
            <Elem
                type={this.props.type && this.props.type === 'submit' ? 'submit' : undefined}
                className={classNames('button', {
                    'disabled': this.props.disabled,
                    'loading': this.props.loading,

                    'layout-default': this.props.layout === 'default' || !this.props.layout,
                    'layout-link': this.props.layout === 'link',

                    'size-small': this.props.size === 'small',

                    'intent-default': this.props.intent === 'default' || !this.props.intent,
                    'intent-primary': this.props.intent === 'primary',
                    'intent-success': this.props.intent === 'success',
                    'intent-danger': this.props.intent === 'danger',
                    'intent-discrete': this.props.intent === 'discrete',
                    'intent-upfront': this.props.intent === 'upfront',
                    'intent-navy': this.props.intent === 'navy',
                    'intent-discrete-navy': this.props.intent === 'discrete-navy',
                    'intent-grey': this.props.intent === 'grey',
                    'intent-transparent': this.props.intent === 'transparent',
                    'intent-white': this.props.intent === 'white',
                }, this.props.className)}
                style={this.props.style}
                onClick={(e) => {
                    if (this.props.menu) {
                        this.handleToggle(e);
                    }
                    if (this.props.onClick) {
                        this.props.onClick(e);
                    }
                }}
                {...(this.props.link ? { to: this.props.href } : { href: this.props.href })}
                name={this.props.name}
                id={this.props.id}
                ref={(node) => this.innerRef = node}
                onBlur={this.props.onBlur}
                onDragEnter={this.props.onDragEnter}
                onDragLeave={this.props.onDragLeave}
                onDragOver={this.props.onDragOver}
                onDrop={this.props.onDrop}
                onFocus={this.props.onFocus}
                onKeyDown={this.props.onKeyDown}
            >
                {this.props.menu ? (
                    <Popover
                        content={this.props.menu}
                        target={(
                            <this.RenderButtonContent/>
                        )}
                        hasBackdrop={false}
                        isOpen={this.state.isOpen}
                        onClose={this.handleClosePopover}
                        portalClassName={'popover-default-portal'}
                        popoverClassName={'popover-default-popover nopadding'}
                        position={BlueprintPosition.BOTTOM}
                        minimal={true}
                        usePortal={true}
                        modifiers={{
                            offset: {
                                offset: '0, 10',
                            },
                        }}
                    />
                ) : (
                    <this.RenderButtonContent/>
                )}
                {this.props.tooltip && (
                    <Tooltip
                        position="top"
                        interactionKind="hover"
                        usePortal={true}
                        content={this.props.tooltip}
                        children={<div />}
                        className="button-tooltip"
                    />
                )}
            </Elem>
        );
    }
}

export default Button;
